import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import { auditEvents } from "../mock/statutory/mockData";
import { ChevronDown } from "lucide-react";

function AuditTracePanel({ event }) {
  const TRACE = [
    { step: "Person Facts", value: event.personName + " — " + event.personId },
    { step: "Classification", value: event.eventType },
    { step: "Eligibility", value: event.determinationId || "—" },
    { step: "Rule Version", value: event.ruleVersionId || "—" },
    { step: "Evidence Ref", value: event.evidenceRef || "—" },
    { step: "Result State", value: event.resultState || event.status },
    { step: "Audit Event", value: event.id + " @ " + new Date(event.timestamp).toLocaleString() },
  ];

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={`audit-trace-${event.id}`}>
      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">End-to-End Trace</div>
      {TRACE.map((t, i) => (
        <div key={i} className="flex items-start gap-2 mb-2">
          <div className="flex flex-col items-center shrink-0 mt-1">
            <div className="h-3 w-3 rounded-full bg-primary/20 border border-primary/50 flex items-center justify-center text-[9px] text-primary font-bold">{i + 1}</div>
            {i < TRACE.length - 1 && <div className="w-px h-4 bg-border" />}
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">{t.step}</div>
            <div className="text-xs font-mono text-foreground">{t.value}</div>
          </div>
        </div>
      ))}
      {event.detail && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">{event.detail}</p>
        </div>
      )}
    </div>
  );
}

function AuditContent() {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [includeNonSuccess] = useState(true);

  const EVENT_TYPE_FILTERS = ["ALL", "CLASSIFICATION_COMPLETE", "ELIGIBILITY_TRANSITION", "CALCULATION_FINALIZED", "CLASSIFICATION_AMBIGUOUS", "DETERMINATION_SUPERSEDED"];

  const filtered = auditEvents.filter((e) => {
    if (filter === "ALL") return true;
    return e.eventType === filter;
  });

  return (
    <div className="space-y-4">
      {/* Query panel */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Audit Query</div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-muted-foreground">Event type:</span>
          {EVENT_TYPE_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${filter === f ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`audit-filter-${f}`}
            >
              {f.replace(/_/g, " ")}
            </button>
          ))}
          <span className="ml-4 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded">
            Non-success events always shown — never filtered by default
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Event list */}
        <div className="lg:col-span-3 space-y-2">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs" data-testid="audit-table">
              <thead className="bg-muted/40">
                <tr>
                  {["Timestamp", "Event Type", "Person", "Regime", "Status", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((ev) => {
                  const isNonSuccess = !!ev.resultState;
                  return (
                    <tr
                      key={ev.id}
                      className={`hover:bg-muted/30 cursor-pointer transition-colors ${selected?.id === ev.id ? "bg-primary/5" : ""} ${isNonSuccess ? "bg-amber-50/30" : ""}`}
                      onClick={() => setSelected(ev)}
                      data-testid={`audit-row-${ev.id}`}
                    >
                      <td className="px-3 py-2.5 font-mono text-muted-foreground whitespace-nowrap">
                        {new Date(ev.timestamp).toLocaleString()}
                      </td>
                      <td className="px-3 py-2.5 font-mono max-w-[160px] truncate">{ev.eventType}</td>
                      <td className="px-3 py-2.5">{ev.personName}</td>
                      <td className="px-3 py-2.5 font-mono max-w-[120px] truncate text-muted-foreground">{ev.regime || "—"}</td>
                      <td className="px-3 py-2.5">
                        <StatutoryBadge state={ev.resultState || "EFFECTIVE"} />
                      </td>
                      <td className="px-3 py-2.5 text-primary">View</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">Non-success audit entries (amber rows) are always visible — suspended contributions remain traceable.</p>
        </div>

        {/* Trace detail */}
        <div className="lg:col-span-2">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Audit Trace</div>
          {selected ? (
            <div className="space-y-4">
              {selected.resultState && (
                <StatutoryStatePanel
                  state={selected.resultState}
                  suspensionNotice={["LEGAL_BASIS_PENDING", "COUNSEL_REVIEW_REQUIRED"].includes(selected.resultState)}
                />
              )}
              <AuditTracePanel event={selected} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Click an audit event to view its full trace.</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
        <p className="text-xs text-blue-700">
          <span className="font-bold">No real calculation engine called.</span> All audit records are mock prototype data. Contribution amounts shown are illustrative only.
        </p>
      </div>
    </div>
  );
}

export default function StatutoryAuditLog() {
  return (
    <StatutoryProvider>
      <PageHeader eyebrow="Statutory Configuration" title="Audit & Calculation Log" description="Statutory audit trail — mock prototype. Non-success events always visible." />
      <StatutoryShell>
        <AuditContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
